import * as flagrApi from './flagrApi'
import { ANONYMOUS_EXPERIMENT_FLAG_KEYS, flags } from './constants'
import { ExperimentFlags, InternalFlags } from './store'

const isExperimentFlag = name => name.startsWith('my-pga-com-exp')

/* eslint-disable no-unused-vars */
const valueFlag = name => ({ name, init: value => value })
const booleanFlag = name => ({ name, init: value => value === 'true' })

const FLAGS = [
  booleanFlag(flags.FLAG_EXPEDITED_SHIPPING_DISABLED),
  booleanFlag(flags.FLAG_FEAT_GROUP_EVENT_RECURRING_PAYMENTS_ENABLED),
  booleanFlag(flags.FLAG_STUDENT_REVENUE_STAT),
  booleanFlag(flags.FLAG_FEAT_BYPASS_JRL_REG),
  booleanFlag(flags.FLAG_JRL_KEY_DATES),
  booleanFlag(flags.FLAG_JRL_HOUSEHOLD_DEMOGRAPHIC_SURVEY),
  booleanFlag(flags.FLAG_FEAT_COACH_BUSINESS_INSIGHTS),
  booleanFlag(flags.FLAG_FEAT_COACH_ANALYTICS),
  booleanFlag(flags.FLAG_FEAT_COACH_NOTES),
  booleanFlag(flags.FLAG_ACADEMY_SCHEDULE_UI),
  valueFlag(flags.FLAG_ACADEMY_SCHEDULE_UI_ACADEMY_ID),
  booleanFlag(flags.FLAG_INTERCOM_MESSENGER_JWT),
  booleanFlag(flags.FLAG_EXP_GROUP_EVENT_REQUIRE_CONTACT_PHONE),
]

const initializeFeatures = async user => {
  // retrieve experiment flag values from previous visits
  // also apply any internal overrides from /ab
  let flagValues = {
    ...ExperimentFlags.get(),
    ...InternalFlags.get(),
  }

  // get the list of active flags for this application
  let activeFlagKeys = new Set()
  try {
    activeFlagKeys = await flagrApi.fetchActiveFlagKeys()

    // perform evaluations for any flags the user has not been evaluated for
    const userFlagKeys = new Set(Object.keys(flagValues))
    const flagKeysToEvaluate = Array.from(activeFlagKeys).filter(
      k => !userFlagKeys.has(k),
    )
    if (flagKeysToEvaluate.length > 0) {
      const flagEvaluations = await flagrApi.evaluateFlags(
        flagKeysToEvaluate,
        user,
      )
      flagValues = {
        ...flagValues,
        ...flagEvaluations,
      }
    }
  } catch (e) {
    window.rg4js &&
      window.rg4js('send', {
        error: e,
        tags: ['flagr'],
      })
  }

  // prune any inactive flags (keep synthetic academy-id companion key)
  flagValues = Object.keys(flagValues)
    .filter(
      f =>
        activeFlagKeys.has(f) ||
        f === flags.FLAG_ACADEMY_SCHEDULE_UI_ACADEMY_ID,
    )
    .reduce((acc, val) => {
      acc[val] = flagValues[val]
      return acc
    }, {})

  if (flagValues[flags.FLAG_ACADEMY_SCHEDULE_UI] !== 'true') {
    delete flagValues[flags.FLAG_ACADEMY_SCHEDULE_UI_ACADEMY_ID]
  }

  const existingExperimentFlags = ExperimentFlags.get() || {}
  if (user != null) {
    const experimentFlagValues = Object.keys(flagValues)
      .filter(f => isExperimentFlag(f))
      .reduce((acc, val) => {
        acc[val] = flagValues[val]
        return acc
      }, {})
    ExperimentFlags.set(experimentFlagValues)
  } else {
    // Anonymous visitors only sticky allowlisted public experiments so other
    // my-pga-com-exp* keys keep logged-in-only cookie behavior.
    const next = Object.keys(existingExperimentFlags)
      .filter(
        key =>
          isExperimentFlag(key) &&
          activeFlagKeys.has(key) &&
          !ANONYMOUS_EXPERIMENT_FLAG_KEYS.has(key),
      )
      .reduce((acc, key) => {
        acc[key] = existingExperimentFlags[key]
        return acc
      }, {})
    ANONYMOUS_EXPERIMENT_FLAG_KEYS.forEach(key => {
      if (flagValues[key] != null) {
        next[key] = flagValues[key]
      }
    })
    ExperimentFlags.set(next)
  }

  const evaluatedFlags = FLAGS.reduce(
    (acc, flag) => ({
      ...acc,
      [flag.name]: flag.init(flagValues[flag.name]),
    }),
    {},
  )

  return evaluatedFlags
}

export default initializeFeatures
